// 求人票スキャン取込のクライアント側 I/O。
// - AI解析（PDFのStorage保存 + Gemini）は Cloud Functions 経由（APIキー非露出）。
// - 確認画面の下書き保存・確定は scans/{scanId} への直接更新（Rules で学校オーナー限定）。

import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getFirestore, doc, collection, query, where,
  onSnapshot, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import type { ScanDraft } from './scanMapping';

const REGION = 'asia-northeast1';
export const MAX_PDF_BYTES = 7 * 1024 * 1024;

export type ScanStatus = 'uploading' | 'processing' | 'review' | 'completed' | 'error';

export type DuplicateCandidate = {
  scanId: string;
  reason: 'fileHash' | 'companyName';
  originalFileName: string;
  status: string;
};

export type ScanDoc = {
  id: string;
  schoolId: string;
  schoolName?: string;
  uploadedBy: string;
  originalFileName: string;
  storagePath: string;
  pdfUrl: string;
  fileSize: number;
  pageCount?: number | null;
  year: string;
  status: ScanStatus;
  error?: { code: string; message: string } | null;
  companyName?: string;
  analysis?: Record<string, any> | null;
  analysisModel?: string;
  overallConfidence?: number | null;
  documentQuality?: { hasJobInfo: boolean; looksLikeResume: boolean; notes?: string | null } | null;
  duplicateCandidates?: DuplicateCandidate[];
  confidence?: Record<string, number>;
  draft?: ScanDraft;
  createdAt?: { seconds: number } | null;
  updatedAt?: { seconds: number } | null;
  completedAt?: { seconds: number } | null;
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました。'));
    reader.readAsDataURL(file);
  });
}

/**
 * PDFをアップロードしてAI解析を実行する。
 * 新規は file を、失敗したスキャンの再解析は scanId を渡す（保存済みPDFを再利用）。
 * 解析完了（scans/{scanId}.status === 'review'）まで待ってから解決する。
 */
export async function analyzeScan(params: {
  schoolId: string;
  file?: File;
  scanId?: string;
}): Promise<{ scanId: string }> {
  const fns = getFunctions(getApp(), REGION);
  const call = httpsCallable(fns, 'analyzeRecruitScan', { timeout: 180000 });

  let fileBase64: string | undefined;
  let fileName: string | undefined;
  if (params.file) {
    if (params.file.type && params.file.type !== 'application/pdf') {
      throw new Error('PDFファイルをアップロードしてください。');
    }
    if (params.file.size > MAX_PDF_BYTES) {
      throw new Error(`ファイルサイズが大きすぎます（上限 ${Math.floor(MAX_PDF_BYTES / 1024 / 1024)}MB）。`);
    }
    fileBase64 = await fileToBase64(params.file);
    fileName = params.file.name;
  }

  const res = await call({
    schoolId: params.schoolId,
    scanId: params.scanId,
    fileBase64,
    fileName,
  });
  return res.data as { scanId: string };
}

export function subscribeScan(scanId: string, cb: (scan: ScanDoc | null) => void) {
  return onSnapshot(doc(getFirestore(), 'scans', scanId), (snap) => {
    cb(snap.exists() ? ({ id: snap.id, ...(snap.data() as any) }) : null);
  });
}

export function subscribeSchoolScans(schoolId: string, cb: (rows: ScanDoc[]) => void) {
  const q = query(collection(getFirestore(), 'scans'), where('schoolId', '==', schoolId));
  return onSnapshot(q, (snap) => {
    const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as ScanDoc[];
    rows.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
    cb(rows);
  });
}

export function saveScanDraft(scanId: string, draft: ScanDraft, confidence?: Record<string, number>) {
  return updateDoc(doc(getFirestore(), 'scans', scanId), {
    draft,
    ...(confidence ? { confidence } : {}),
    status: 'review',
    updatedAt: serverTimestamp(),
  });
}

export function completeScan(scanId: string, draft: ScanDraft) {
  return updateDoc(doc(getFirestore(), 'scans', scanId), {
    draft,
    status: 'completed',
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export function reopenScan(scanId: string) {
  return updateDoc(doc(getFirestore(), 'scans', scanId), {
    status: 'review',
    updatedAt: serverTimestamp(),
  });
}

export function deleteScan(scanId: string) {
  return deleteDoc(doc(getFirestore(), 'scans', scanId));
}
